import type { ReactNode } from "react";
import { Footer } from "./Footer";

interface AppShellProps {
  familyName: string;
  children: ReactNode;
  banners?: ReactNode;
  overlays?: ReactNode;
  /**
   * Whether to render the Footer ("(c) 2026 Nuthatch LLC" line). Defaults to
   * `false`; the main tabbed screen no longer shows it (card 312), so any
   * screen that still wants it has to opt in explicitly.
   */
  footer?: boolean;
}

/**
 * Layout wrapper used by every screen inside TabApp.
 * Renders the outer shell, optional banner slot, the family-name heading,
 * and the tab-content area that hosts the active screen.
 *
 * `overlays` is for viewport-pinned, `position: fixed` UI (the floating tab
 * bar, toasts). It is deliberately a separate slot from `children`: anything
 * rendered inside `.tab-content` lives in a scroll container that carries
 * `-webkit-overflow-scrolling: touch`, and WebKit clips fixed-position
 * descendants of such a container to its bounds instead of pinning them to
 * the viewport. Keeping overlays as siblings of `.tab-content` means shell
 * layout changes can never eat into what they show.
 */
export function AppShell({ familyName, children, banners, overlays, footer = false }: AppShellProps) {
  return (
    <div className="app-shell">
      {banners}
      <h1 className="family-name">{familyName}</h1>
      <div className="tab-content">{children}</div>
      {overlays}
      {footer && <Footer />}
    </div>
  );
}
